<template>
  <div
    class="medal-card"
    :class="{
      'medal-card--earned': isEarned,
      'medal-card--locked': !isEarned,
      [`medal-card--${levelSlug}`]: isEarned,
    }"
    @click="$emit('click')"
    role="button"
    tabindex="0"
    @keydown.enter="$emit('click')"
  >
    <!-- Level chip -->
    <span v-if="levelName" class="medal-card__chip" :style="{ background: levelColor, color: chipTextColor }">
      {{ levelName }}
    </span>

    <!-- Medal image -->
    <div class="medal-card__image-wrap">
      <img :src="image" :alt="title" class="medal-card__image" />
    </div>

    <!-- Title -->
    <p class="medal-card__title">{{ title }}</p>

    <!-- Career path track (role card only) -->
    <template v-if="careerPath">
      <div class="role-track">
        <template v-for="(role, i) in careerPath.track">
          <div
            :key="'dot_' + role"
            class="role-track__dot"
            :class="{
              'role-track__dot--past':    i < careerPath.currentIndex,
              'role-track__dot--current': i === careerPath.currentIndex,
              'role-track__dot--future':  i > careerPath.currentIndex,
            }"
            :title="role"
          />
          <div
            v-if="i < careerPath.track.length - 1"
            :key="'line_' + i"
            class="role-track__line"
            :class="{ 'role-track__line--past': i < careerPath.currentIndex }"
          />
        </template>
        <!-- Fork indicator when consultant is still on the common segment -->
        <template v-if="careerPath.branches">
          <div class="role-track__line" />
          <span class="role-track__fork" title="Dois caminhos: FE ou FO">⑂</span>
        </template>
      </div>

      <!-- Hint text below the track -->
      <p v-if="careerPath.branches" class="medal-card__hint">
        Em seguida: FE ou FO
      </p>
      <p v-else-if="careerPath.currentIndex < careerPath.track.length - 1" class="medal-card__hint">
        Próximo: {{ careerPath.track[careerPath.currentIndex + 1] }}
      </p>
      <p v-else class="medal-card__hint" style="color: #FFD700;">
        Topo da carreira
      </p>
    </template>

    <!-- Progress ring -->
    <MedalProgressRing
      v-if="nextThreshold !== null"
      :percentage="progressPct"
      :current-count="currentCount"
      :next-threshold="nextThreshold"
      :color="isEarned ? levelColor : '#718096'"
      :size="90"
      :dark-mode="isDark"
    />
    <div v-else-if="progressPct >= 100" class="medal-card__max-badge">
      <feather-icon icon="AwardIcon" size="18" />
      Nível máximo
    </div>

    <!-- "Faltam X para Nível" label -->
    <p v-if="nextThreshold !== null && !isEarned" class="medal-card__hint">
      Faltam {{ fmtNum(nextThreshold - currentCount) }} p/ {{ nextLevelName }}
    </p>
    <p v-else-if="nextThreshold !== null && isEarned" class="medal-card__hint">
      {{ fmtNum(nextThreshold - currentCount) }} p/ {{ nextLevelName }}
    </p>

    <!-- Achievement date -->
    <p v-if="achievedAt" class="medal-card__date">
      Conquistado {{ formattedDate }}
    </p>
  </div>
</template>

<script>
import MedalProgressRing from './MedalProgressRing.vue'

const LEVEL_COLORS = {
  bronze:   '#CD7F32',
  prata:    '#C0C0C0',
  ouro:     '#FFD700',
  platina:  '#7EB8D4',
  diamante: '#A78BFA',
}

export default {
  name: 'MedalCard',
  components: { MedalProgressRing },
  emits: ['click'],
  props: {
    title:         { type: String, required: true },
    image:         { type: String, required: true },
    levelName:     { type: String, default: null },
    currentCount:  { type: Number, default: 0 },
    nextThreshold: { type: Number, default: null },
    nextLevelName: { type: String, default: null },
    progressPct:   { type: Number, default: 0 },
    achievedAt:    { type: String, default: null },
    careerPath:    { type: Object, default: null },
  },
  computed: {
    isEarned()    { return this.progressPct === 100 || (this.levelName && this.currentCount > 0) },
    levelSlug()   { return (this.levelName || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '') },
    levelColor()  { return LEVEL_COLORS[this.levelSlug] || '#718096' },
    chipTextColor() {
      return this.levelSlug === 'prata' ? '#1a202c' : '#fff'
    },
    isDark() {
      return typeof document !== 'undefined' && document.body.classList.contains('dark-layout')
    },
    formattedDate() {
      if (!this.achievedAt) return null
      return new Date(this.achievedAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
    },
  },
  methods: {
    fmtNum(n) {
      if (n == null) return ''
      if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
      if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K'
      return String(Math.round(n))
    },
  },
}
</script>

<style scoped>
.medal-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 12px 8px 10px;
  border-radius: 14px;
  background: #f4f5f7;
  border: 1.5px solid #dde1e7;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-width: 110px;
  text-align: center;
}

.dark-layout .medal-card {
  background: #1a2035;
  border-color: #2d3748;
}

.medal-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.dark-layout .medal-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

/* Earned states with glow */
.medal-card--bronze  { border-color: #CD7F32; }
.medal-card--prata   { border-color: #C0C0C0; }
.medal-card--ouro    { border-color: #FFD700; }
.medal-card--platina { border-color: #7EB8D4; }
.medal-card--diamante{ border-color: #A78BFA; }

.medal-card--earned .medal-card__image {
  animation: medalGlow 2.8s ease-in-out infinite, medalUnlock 0.55s ease-out both;
}

.medal-card--locked {
  opacity: 0.75;
}
.medal-card--locked .medal-card__image {
  filter: grayscale(50%);
}

/* Level chip */
.medal-card__chip {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 5px;
  border-radius: 20px;
  text-transform: uppercase;
  white-space: nowrap;
}

/* Image */
.medal-card__image-wrap {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.medal-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

/* Text */
.medal-card__title {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #2d3748;
}
.dark-layout .medal-card__title {
  color: #e2e8f0;
}

.medal-card__hint {
  margin: 0;
  font-size: 10px;
  color: #718096;
  line-height: 1.3;
}
.medal-card__date {
  margin: 0;
  font-size: 9px;
  color: #8a96a8;
  font-style: italic;
}
.dark-layout .medal-card__date {
  color: #4a5568;
}

.medal-card__max-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #FFD700;
  font-weight: 600;
}

/* Career path track */
.role-track {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 6px 4px 2px;
}
.role-track__dot {
  flex-shrink: 0;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  border: 1.5px solid #b0b8c8;
  background: #f4f5f7;
}
.dark-layout .role-track__dot {
  border-color: #4a5568;
  background: #1a2035;
}

.role-track__dot--past {
  background: #b0b8c8;
  border-color: #b0b8c8;
}
.dark-layout .role-track__dot--past {
  background: #4a5568;
  border-color: #4a5568;
}

.role-track__dot--current {
  width: 10px;
  height: 10px;
  background: #2d3748;
  border-color: #2d3748;
  box-shadow: 0 0 7px rgba(45, 55, 72, 0.5);
}
.dark-layout .role-track__dot--current {
  background: #e2e8f0;
  border-color: #e2e8f0;
  box-shadow: 0 0 7px rgba(226, 232, 240, 0.7);
}

.role-track__dot--future {
  background: #f4f5f7;
  border-color: #dde1e7;
}
.dark-layout .role-track__dot--future {
  background: #1a2035;
  border-color: #2d3748;
}

.role-track__line {
  flex: 1;
  height: 1.5px;
  background: #dde1e7;
  min-width: 2px;
}
.dark-layout .role-track__line {
  background: #2d3748;
}

.role-track__line--past {
  background: #b0b8c8;
}
.dark-layout .role-track__line--past {
  background: #4a5568;
}

.role-track__fork {
  font-size: 13px;
  color: #b0b8c8;
  flex-shrink: 0;
  line-height: 1;
}
.dark-layout .role-track__fork {
  color: #718096;
}

/* Animations */
@keyframes medalGlow {
  0%, 100% { box-shadow: 0 0 6px rgba(255, 200, 0, 0.25); }
  50%       { box-shadow: 0 0 20px rgba(255, 200, 0, 0.7), 0 0 40px rgba(255, 200, 0, 0.25); }
}

@keyframes medalUnlock {
  0%   { transform: scale(0.82); opacity: 0.5; }
  60%  { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
}
</style>
